import debounce from 'lodash.debounce';
import { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { ProductCardHorizontal, ProductCardHorizontalDetails, ProductCardHorizontalImage } from '@mweb/zappos-ui/ProductCardHorizontal';

import Button from 'components/common/Button';
import useMartyContext from 'hooks/useMartyContext';
import UtilityDottedHeartOutlineMediumIcon from 'tailwind/components/Icons/UtilityDottedHeartOutlineMediumIcon';
import HeartOutlineIcon from 'components/common/IconsV2/HeartOutlineIcon';
import { cartError, cartIsLoading, changeQuantity } from 'actions/cart';
import { onModifyQuantity, onRemoveFromCart } from 'store/ducks/cart/actions';
import { fetchHeartList, heartProduct, unHeartProduct } from 'actions/hearts';
import { HEART_DEFAULT_LIST_ID } from 'constants/apis';
import toUSD from 'utils/toUSD';
import FinalSale from 'components/common/FinalSale';
import { translateCartError } from 'apis/mafia';
import { SmallLoader } from 'components/Loader';
import type { ItemProps } from 'types/mafia';
import { QuantitySelector } from 'components/common/QuantitySelector/QuantitySelector';
import { cn } from 'helpers/classnames';
import { ProductCardPrice } from 'components/cart/NewCartItem/ProductCardPrice';
import { ProductCardHorizontalDetailsItem } from 'components/ProductCardHorizontal/ProductCardHorizontalDetailsItem';

interface Props {
  item: ItemProps;
  isRecommendedFit?: boolean;
  isUnavailable?: boolean;
  className?: string;
}

type PropsFromRedux = ConnectedProps<typeof connector>;
export type NewCartItemProps = Props & PropsFromRedux;

interface LowStockWarningProps {
  isUnavailable?: boolean;
  availableAmount: number;
  testId: (id: string) => string;
}

interface LoaderOverlayProps {
  asin: string;
  updatingAsin: string;
}

const LowStockWarning = ({ availableAmount, testId }: LowStockWarningProps) => (
  <span data-test-id={testId('lowStock')} className="absolute left-1 top-1 rounded bg-alert p-1 text-alert-inline">
    {availableAmount} left
  </span>
);

const LoaderOverlay = ({ asin, updatingAsin }: LoaderOverlayProps) => (
  <>
    {updatingAsin === asin && (
      <div className="absolute bottom-0 left-0 right-0 top-0 z-10 bg-default-minimal opacity-[0.85]">
        <SmallLoader />
      </div>
    )}
  </>
);

function NewCartItem({
  item,
  isUnavailable,
  isRecommendedFit,
  onRemoveFromCart,
  changeQuantity,
  heartProduct,
  fetchHeartList,
  cartIsLoading,
  cartError,
  onModifyQuantity,
  className
}: NewCartItemProps) {
  const {
    testId,
    marketplace: {
      shortName,
      pdp: { egcUrl }
    }
  } = useMartyContext();

  const {
    asin,
    cartItemId,
    brandName,
    catalogBundle: {
      image: { alt: imageAlt, href, url },
      style
    },
    color,
    deliveryDate,
    egc,
    finalSale,
    glProductGroupType,
    merchantId,
    originalPrice,
    price,
    productId,
    quantity,
    recipientEmail,
    sizing: {
      languageTagged: { displaySize, shoeWidth }
    }
  } = item;

  const isItemGiftCard = egc && glProductGroupType === 'gl_gift_card';
  const stock = quantity || 1;
  const availableAmount = item.stock || quantity;

  const [updatingAsin, setUpdatingAsin] = useState('');
  const [baseCount, setBaseCount] = useState(quantity);

  useEffect(() => {
    setBaseCount(quantity);
  }, [quantity, item]);

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    // Cleanup debounced function on unmount to prevent state updates after unmount
    return () => {
      if (onQuantityChange?.cancel) {
        onQuantityChange.cancel();
      }
    };
  }, []);

  let link: string = '';
  let alt: string = '';

  if (egc) {
    link = egcUrl;
    alt = `${shortName} Gift Card`;
  } else {
    link = asin ? href : `/product/${productId}`;
    alt = imageAlt;
  }

  const onItemUpdate = (asin: string) => {
    cartIsLoading();
    setUpdatingAsin(asin);
  };

  const onItemUpdateDone = () => {
    setUpdatingAsin('');
  };

  const onQuantityChange = debounce((data: { value: number; isIncrement: boolean }) => {
    setBaseCount(data.value);
    if (data.value === 0) {
      onRemoveItem(asin);
      return;
    }
    onModifyQuantity(data.value, asin);
    onItemUpdate(asin);
    changeQuantity({ items: [{ asin, quantity: data.value, cartItemId }] }).then(response => {
      onItemUpdateDone();

      const error = translateCartError(response);
      if (error) {
        cartError(error);
      }
    });
  }, 250);

  const onRemoveItem = (asin: string) => {
    setUpdatingAsin(asin);
    const itemData = { asin, quantity: 0, cartItemId };
    changeQuantity({ items: [itemData] });
    onRemoveFromCart(asin, true);
  };

  const onHeartItem = (asin: string) => {
    setUpdatingAsin(asin);
    heartProduct({ subItemId: asin, listId: HEART_DEFAULT_LIST_ID, merchantId }, fetchHeartList);
    onRemoveItem(asin); // After hearting the product, removes the item from cart
  };

  const TailIcon = (
    <>
      <UtilityDottedHeartOutlineMediumIcon className="stroke-2 lumon:hidden" size={24} />
      <HeartOutlineIcon className="not-lumon:hidden" size={24} />
    </>
  );

  const testIdName = egc ? 'giftCardItem' : 'cartItem';

  return (
    <div
      data-test-id={testId(testIdName)}
      className={cn(
        'relative flex flex-col justify-between gap-y-4 border-b border-button-subtle pb-6 last:border-b-0 md:flex-row md:gap-y-0',
        className
      )}
    >
      <LoaderOverlay asin={asin} updatingAsin={updatingAsin} />

      <ProductCardHorizontal className="relative w-full gap-x-6">
        <ProductCardHorizontalImage
          data-ted={asin}
          data-te={'TE_CART_PRODUCTCLICKED'}
          data-test-id={testId('cartItemImage')}
          href={link}
          alt={alt}
          src={url}
          imageClassName="aspect-[4/5] w-[80px] h-[100px]"
        >
          {!isUnavailable && availableAmount > 0 && availableAmount < 4 && (
            <LowStockWarning testId={testId} isUnavailable={isUnavailable} availableAmount={availableAmount} />
          )}
        </ProductCardHorizontalImage>
        <ProductCardHorizontalDetails className="w-full">
          <ProductCardHorizontalDetailsItem
            testId="brand"
            label="Brand"
            value={brandName}
            url={link}
            data-te="TE_CART_PRODUCTCLICKED"
            data-ted={asin}
          />
          <ProductCardHorizontalDetailsItem
            testId="productName"
            label="Product Name"
            value={style}
            isBoldValue
            className="mb-2"
            url={link}
            valueClassName="hover:text-button-active"
            data-te="TE_CART_PRODUCTCLICKED"
            data-ted={asin}
          />
          {!isItemGiftCard && <ProductCardHorizontalDetailsItem testId="color" label="Color" value={color} prefixLabel="Color" isBoldPrefixLabel />}
          {!isItemGiftCard && (
            <ProductCardHorizontalDetailsItem
              testId="size"
              label={isRecommendedFit ? 'Recommended Size' : 'Size'}
              value={displaySize}
              prefixLabel="Size"
              isBoldPrefixLabel
            />
          )}
          {!isItemGiftCard && shoeWidth && <ProductCardHorizontalDetailsItem testId="width" label="Width" value={shoeWidth} prefixLabel="Width" />}
          {isItemGiftCard && recipientEmail && (
            <ProductCardHorizontalDetailsItem testId="recipientEmail" label="Recipient Email" value={recipientEmail} prefixLabel="Recipient Email" />
          )}

          {isUnavailable ? (
            <p data-test-id={testId('unavailable')} className="text-xs font-bold uppercase text-alert-inline md:hidden">
              Unavailable
            </p>
          ) : (
            <ProductCardPrice testId={testId('price')} className="my-2 flex-col md:hidden" price={stock * price} msrp={stock * originalPrice}>
              {stock !== 1 && <span className="text-xs font-normal text-tertiary">({toUSD(price)} each)</span>}
            </ProductCardPrice>
          )}
          {finalSale && <FinalSale className="flex w-fit md:hidden" />}

          {isItemGiftCard && deliveryDate && (
            <ProductCardHorizontalDetailsItem testId="deliveryDate" label="Delivery Date" value={deliveryDate} prefixLabel="Delivery Date" />
          )}

          {!isItemGiftCard && (
            <QuantitySelector
              size="small"
              variant={isUnavailable ? 'disabled' : 'base'}
              className="md:hidden"
              baseCount={isUnavailable ? 0 : baseCount}
              testId={testId('quantitySelectorMobile')}
              maxCount={availableAmount <= 10 ? availableAmount : 10}
              data-asin={asin}
              onQuantityChange={data => onQuantityChange(data)}
            />
          )}

          <div className="hidden items-center gap-x-6 md:flex">
            <Button
              variant="text"
              aria-label="Remove Item"
              data-test-id={testId('removeButton')}
              type="button"
              className="font-semibold"
              onClick={() => onRemoveItem(asin)}
            >
              Remove
            </Button>
            <Button
              variant="text"
              data-test-id={testId('moveToFavoritesButton')}
              className="font-semibold"
              onClick={() => onHeartItem(asin)}
              tailIcon={TailIcon}
            >
              Move To
            </Button>
          </div>
        </ProductCardHorizontalDetails>
      </ProductCardHorizontal>
      <div className="hidden flex-col items-end gap-y-4 md:flex">
        {isUnavailable ? (
          <p data-test-id={testId('unavailable')} className="text-xs font-bold uppercase text-alert-inline">
            Unavailable
          </p>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <ProductCardPrice testId={testId('price')} className="flex-col !items-end" price={stock * price} msrp={stock * originalPrice}>
              {stock !== 1 && <span className="text-xs font-normal text-tertiary">({toUSD(price)} each)</span>}
            </ProductCardPrice>
            {finalSale && <FinalSale />}
          </div>
        )}
        {!isItemGiftCard && (
          <QuantitySelector
            variant={isUnavailable ? 'disabled' : 'base'}
            size="small"
            baseCount={isUnavailable ? 0 : baseCount}
            className="w-full"
            containerClassName="w-full"
            testId={testId('quantitySelector')}
            maxCount={availableAmount <= 10 ? availableAmount : 10}
            data-asin={asin}
            onQuantityChange={data => onQuantityChange(data)}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-x-6 md:hidden">
        <Button
          variant="text"
          data-test-id={testId('removeButtonMobile')}
          aria-label="Remove Item"
          type="button"
          className="font-semibold"
          onClick={() => onRemoveItem(asin)}
        >
          Remove
        </Button>
        <Button
          variant="text"
          data-test-id={testId('moveToFavoritesButtonMobile')}
          className="font-semibold"
          onClick={() => onHeartItem(asin)}
          tailIcon={TailIcon}
        >
          Move To
        </Button>
      </div>
    </div>
  );
}

const mapDispatchToProps = {
  cartError,
  cartIsLoading,
  changeQuantity,
  onModifyQuantity,
  onRemoveFromCart,
  heartProduct,
  unHeartProduct,
  fetchHeartList
};

const connector = connect(null, mapDispatchToProps);

export default connector(NewCartItem);
